import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
    servicerRoles,
    conventionStatus,
    RoleNames,
    UserEnrichments,
    ORDER_BY,
    investorClassifications,
    providerRoles,
    subscriptionStatusLabels,
    promoterRoles,
    roleCategories,
    sortDirection,
} from 'utils/constants';
import { getProfileByRoles, parseFullName, parseRoleByProfile } from 'utils/helpers';
import { useMatchAtLeastOneRole } from 'utils/hooks';
import { distributorSchemas, userSchemas } from 'schemas';
import {
    useGetDistributorCompaniesQuery,
    useGetInvestmentFundsQuery,
    useGetPromotersQuery,
    useGetRolesQuery,
    useGetSGPsQuery,
    useGetShareSubcategoriesQuery,
    useGetSubscriptionsQuery,
    useGetUsersQuery,
} from 'api/main';
import { texts } from 'utils/languages/fr';

import {
    ActionButton,
    CancelButton,
    CustomDatePicker,
    CustomSelect,
    CustomTextField,
    Loader,
} from 'components';
import { Box, Collapse, InputAdornment, MenuItem, Paper, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    wrapper: {
        marginBottom: theme.spacing(4),
    },
    filterInput: {
        width: 200,
        marginBottom: 0,
        '&:not(:last-child)': {
            marginRight: theme.spacing(2),
        },
    },
    input: {
        width: 200,
        marginBottom: 0,
    },
    filtersPaper: {
        width: '100%',
        padding: theme.spacing(2),
    },
    dash: {
        marginRight: theme.spacing(2),
    },
    dashBottom: {
        marginBottom: theme.spacing(),
        marginRight: theme.spacing(2),
    },
}));

const getDefaultFilters = obj => ({
    minSum: obj?.minSum || '',
    maxSum: obj?.maxSum || '',
    minAdmissionFee: obj?.minAdmissionFee || '',
    maxAdmissionFee: obj?.maxAdmissionFee || '',
    issueDateStart: obj?.issueDateStart ? moment(obj.issueDateStart) : null,
    issueDateEnd: obj?.issueDateEnd ? moment(obj.issueDateEnd) : null,
    entryDateStart: obj?.entryDateStart ? moment(obj.entryDateStart) : null,
    entryDateEnd: obj?.entryDateEnd ? moment(obj.entryDateEnd) : null,
    alertTypes: obj?.alertTypes?.length ? obj.alertTypes : 'all',
    companyId: obj?.companyId || 'all',
    role: obj?.role || 'all',
    subcategory: obj?.subcategory || 'all',
    product: obj?.product || 'all',
    investorClassification: obj?.investorClassification || 'all',
    distributorUser: obj?.distributorUser || 'all',
    tag: obj?.tag || '',
    subscriptionStatus: obj?.subscriptionStatus || 'all',
    provider: obj?.provider || 'all',
    promoter: obj?.promoter || 'all',
});

const getCompanyIdFilterValue = companyId => {
    if (companyId === 'all') return undefined;
    if (companyId === 0) return companyId;
    return [companyId];
};

const buildFilterPayload = (filters, roles) => ({
    ...filters,
    role: parseRoleByProfile(filters.role, roles),
    minAdmissionFee: filters.minAdmissionFee === '' ? undefined : filters.minAdmissionFee,
    maxAdmissionFee: filters.maxAdmissionFee === '' ? undefined : filters.maxAdmissionFee,
    alertTypes: filters.alertTypes === 'all' ? undefined : filters.alertTypes,
    investorClassification:
        filters.investorClassification === 'all' ? undefined : filters.investorClassification,
    product: filters.product === 'all' ? undefined : [filters.product],
    issueDateStart: filters?.issueDateStart ? moment(filters.issueDateStart).toISOString() : null,
    issueDateEnd: filters?.issueDateEnd ? moment(filters.issueDateEnd).toISOString() : null,
    entryDateStart: filters?.entryDateStart ? moment(filters.entryDateStart).toISOString() : null,
    entryDateEnd: filters?.entryDateEnd ? moment(filters.entryDateEnd).toISOString() : null,
    companyId: getCompanyIdFilterValue(filters.companyId),
    subcategory: filters.subcategory === 'all' ? undefined : [filters.subcategory],
    dist: filters.distributorUser === 'all' ? undefined : [filters.distributorUser],
    subscriptionStatus:
        filters.subscriptionStatus === 'all' ? undefined : filters.subscriptionStatus,
    provider: filters.provider === 'all' ? undefined : filters.provider,
    promoter: filters.promoter === 'all' ? undefined : filters.promoter,
});

const userSorter = (a, b) => {
    if (parseFullName(a) > parseFullName(b)) return 1;
    if (parseFullName(a) < parseFullName(b)) return -1;
    return 0;
};

const CustomFilters = props => {
    const {
        open,
        applyFilters,
        savedFilters,
        loading,
        ongoing,
        displaySubscriptionStatus,
        defaultFilters,
    } = props;
    const classes = useStyles();
    const mounted = useRef(false);

    const userIsServicer = useMatchAtLeastOneRole(servicerRoles);
    const userIsProvider = useMatchAtLeastOneRole(providerRoles);
    const userIsPromoter = useMatchAtLeastOneRole(promoterRoles);
    const userIsNetworkAdmin = useMatchAtLeastOneRole([RoleNames.NETWORK_ADMIN]);
    const userIsDistributor = useMatchAtLeastOneRole([
        RoleNames.ADMIN_CIF,
        RoleNames.PSI,
        RoleNames.COMMERCIALISATEUR,
    ]);

    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const ppRoles = useMemo(() => roles.filter(r => r.name.includes('PP')), [roles]);
    const pmRoles = useMemo(() => roles.filter(r => r.name.includes('PM')), [roles]);
    const adminDistributorRoles = useMemo(
        () =>
            roles.filter(
                r =>
                    r.category?.name === roleCategories.ADMIN_DISTRIBUTORS ||
                    r.category?.name === roleCategories.DISTRIBUTORS
            ),
        [roles]
    );

    const [customFilters, setCustomFilters] = useState(
        getDefaultFilters({
            ...savedFilters,
            role: getProfileByRoles(savedFilters.role, ppRoles, pmRoles),
        })
    );

    const {
        data: { userList: distributorUsers = [] } = { userList: [] },
        isFetching: distributorUsersLoader,
    } = useGetUsersQuery(
        userSchemas.getUsers({
            pagination: false,
            role: roles
                .filter(
                    r =>
                        r.name === RoleNames.COMMERCIALISATEUR ||
                        r.name === RoleNames.ADMIN_CIF ||
                        r.name === RoleNames.PSI
                )
                .map(r => r.id),
            enrichWith: [UserEnrichments.ROLES, UserEnrichments.DISTRIBUTOR_COMPANY],
            companyId: customFilters.companyId,
        }),
        { skip: !roles.length || !customFilters.companyId || customFilters.companyId === 'all' }
    );
    const {
        data: { subscriptions: distributorSubscriptions = [] } = { subscriptions: [] },
        isFetching: distributorSubscriptionsLoader,
    } = useGetSubscriptionsQuery(
        { role: adminDistributorRoles.map(role => role.id), pagination: false },
        { skip: !roles.length || !userIsDistributor }
    );
    const {
        data: { distributors: distributorCompanies = [] } = { distributors: [] },
        isLoading: companyLoader,
        isFetching: fetchingCompanies,
    } = useGetDistributorCompaniesQuery(
        distributorSchemas.getDistributorList({
            pagination: false,
            orderBy: ORDER_BY.distributorCompanyName,
            conventionWithFund:
                customFilters.product === 'all' ? undefined : [+customFilters.product],
        }),
        { skip: !userIsServicer && !userIsProvider && !userIsPromoter }
    );
    const {
        data: { promoters = [] } = { promoters: [] },
        isLoading: promotersLoader,
        isFetching: promotersFetching,
    } = useGetPromotersQuery(
        {
            pagination: false,
            hasSubscriptions: true,
            orderBy: ORDER_BY.name,
            direction: sortDirection.ASC,
        },
        { skip: !userIsServicer }
    );
    const {
        data: { sgps: providers = [] } = { sgps: [] },
        isLoading: providersLoader,
        isFetching: providersFetching,
    } = useGetSGPsQuery(
        {
            pagination: false,
            hasSubscriptions: true,
            orderBy: ORDER_BY.name,
            direction: sortDirection.ASC,
        },
        { skip: !userIsServicer }
    );

    const { data: fundList = [], isLoading: fundsLoader } = useGetInvestmentFundsQuery({
        visible: 1,
    });

    const sortedDistributorUsers = useMemo(
        () => [...distributorUsers].sort(userSorter),
        [distributorUsers]
    );

    const { data: shareSubcategories = [], isFetching: subcategoriesLoader } =
        useGetShareSubcategoriesQuery(
            { 'shareCategory.investmentFund': customFilters.product },
            { skip: customFilters.product === 'all' || !customFilters.product }
        );

    const processedDistributorCompanies = useMemo(
        () =>
            distributorCompanies.filter(
                ({ 0: { conventions } }) =>
                    !!conventions.find(c => c.status === conventionStatus.FULLY_SIGNED)
            ),
        [distributorCompanies]
    );

    const handleChange = useCallback(
        key => e => {
            setCustomFilters(prevState => {
                let newSubcategory = prevState.subcategory;
                let newCompanyId = prevState.companyId;
                if (key === 'product') {
                    newSubcategory = 'all';
                    newCompanyId = 'all';
                }
                if (key === 'subcategory') {
                    newSubcategory = e.target.value;
                }
                if (key === 'companyId') {
                    newCompanyId = e.target.value;
                }
                return {
                    ...prevState,
                    [key]: e.target.value,
                    subcategory: newSubcategory,
                    companyId: newCompanyId,
                };
            });
        },
        []
    );

    const handleDateChange = useCallback(
        key => newDate => {
            if (key.includes('Start')) {
                const endKey = key.replace('Start', 'End');
                if (newDate && customFilters[endKey] && newDate.isAfter(customFilters[endKey])) {
                    setCustomFilters(oldValue => ({ ...oldValue, [endKey]: newDate }));
                }
            }
            setCustomFilters(oldValue => ({ ...oldValue, [key]: newDate }));
        },
        [customFilters]
    );

    const handleCancel = useCallback(() => {
        let defaults = getDefaultFilters(defaultFilters);
        setCustomFilters(defaults);
        applyFilters(buildFilterPayload(defaults, roles));
    }, [applyFilters, roles, defaultFilters]);

    const handleApplyFilters = useCallback(() => {
        applyFilters(buildFilterPayload(customFilters, roles));
    }, [applyFilters, customFilters, roles]);

    useEffect(() => {
        if (ppRoles.length && pmRoles.length) {
            const parsedRoles = getProfileByRoles(savedFilters.role, ppRoles, pmRoles);
            setCustomFilters(prevState => ({
                ...prevState,
                role: parsedRoles,
            }));
        }
    }, [ppRoles, pmRoles, savedFilters]);

    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    }, []);

    return (
        <Collapse in={open} classes={{ wrapper: classes.wrapper }}>
            <Paper elevation={1} className={classes.filtersPaper}>
                <Loader
                    loading={
                        companyLoader ||
                        fundsLoader ||
                        rolesLoader ||
                        promotersLoader ||
                        providersLoader
                    }
                >
                    <Box display="flex" flexWrap="wrap" mb={2} gap="16px">
                        <CustomSelect
                            value={customFilters.alertTypes}
                            label={texts.ALERTS.ALERTS}
                            onChange={handleChange('alertTypes')}
                            inputStyle={classes.input}
                        >
                            <MenuItem value="all">{texts.ALL_2}</MenuItem>
                            <MenuItem value="critical">{texts.ALERTS.CRITICAL_ALERTS}</MenuItem>
                            <MenuItem value="warning">{texts.ALERTS.WARNING_ALERTS}</MenuItem>
                            <MenuItem value="none">{texts.ALERTS.NO_ALERTS}</MenuItem>
                        </CustomSelect>
                        <CustomSelect
                            value={customFilters.role}
                            label={texts.PROFIL_INVESTISSEUR}
                            onChange={handleChange('role')}
                            inputStyle={classes.input}
                        >
                            <MenuItem value="all">{texts.ALL_1}</MenuItem>
                            <MenuItem value="PP">{texts.PERSONNE_PHYSIQUE}</MenuItem>
                            <MenuItem value="PM">{texts.PERSONNE_MORALE}</MenuItem>
                        </CustomSelect>
                    </Box>
                    <Typography variant="subtitle1">{texts.DATE_ENTREE}</Typography>
                    <Box display="flex" alignItems="flex-end" flexWrap="wrap" mb={2}>
                        <CustomDatePicker
                            label={texts.START_DATE}
                            inputStyle={classes.filterInput}
                            value={
                                ongoing
                                    ? customFilters.issueDateStart
                                    : customFilters.entryDateStart
                            }
                            onChange={handleDateChange(
                                ongoing ? 'issueDateStart' : 'entryDateStart'
                            )}
                            maxDate={moment()}
                        />
                        <span className={classes.dashBottom}>-</span>
                        <CustomDatePicker
                            label={texts.END_DATE}
                            inputStyle={classes.filterInput}
                            value={
                                ongoing ? customFilters.issueDateEnd : customFilters.entryDateEnd
                            }
                            onChange={handleDateChange(ongoing ? 'issueDateEnd' : 'entryDateEnd')}
                            minDate={
                                ongoing
                                    ? customFilters.issueDateStart || undefined
                                    : customFilters.entryDateStart || undefined
                            }
                            maxDate={moment()}
                        />
                    </Box>
                    <Typography variant="subtitle1">{texts.MONTANT}</Typography>
                    <Box display="flex" alignItems="center" mb={2}>
                        <CustomTextField
                            formatNumber
                            placeholder={texts.MIN_SUM}
                            inputStyle={classes.filterInput}
                            value={customFilters.minSum}
                            onChange={handleChange('minSum')}
                            endAdornment={<InputAdornment position="start">€</InputAdornment>}
                        />
                        <span className={classes.dash}>-</span>
                        <CustomTextField
                            formatNumber
                            placeholder={texts.MAX_SUM}
                            inputStyle={classes.filterInput}
                            value={customFilters.maxSum}
                            onChange={handleChange('maxSum')}
                            endAdornment={<InputAdornment position="start">€</InputAdornment>}
                        />
                    </Box>
                    <Typography variant="subtitle1">{texts.ADMISSION_FEE}</Typography>
                    <Box display="flex" alignItems="center" mb={2}>
                        <CustomTextField
                            formatNumber
                            placeholder={texts.MIN_PERCENTAGE}
                            inputStyle={classes.filterInput}
                            value={customFilters.minAdmissionFee}
                            onChange={handleChange('minAdmissionFee')}
                            endAdornment={<InputAdornment position="start">%</InputAdornment>}
                        />
                        <span className={classes.dash}>-</span>
                        <CustomTextField
                            formatNumber
                            placeholder={texts.MAX_PERCENTAGE}
                            inputStyle={classes.filterInput}
                            value={customFilters.maxAdmissionFee}
                            onChange={handleChange('maxAdmissionFee')}
                            endAdornment={<InputAdornment position="start">%</InputAdornment>}
                        />
                    </Box>
                    <Box display="flex" flexWrap="wrap" mb={2} gap="16px">
                        <CustomSelect
                            value={customFilters.product}
                            label={texts.PRODUIT}
                            onChange={handleChange('product')}
                            inputStyle={classes.input}
                        >
                            <MenuItem value="all">{texts.ALL_1}</MenuItem>
                            {fundList.map(fund => (
                                <MenuItem key={fund.id} value={fund.id}>
                                    {fund.name}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                        <CustomSelect
                            disabled={!customFilters.product || subcategoriesLoader}
                            value={customFilters.subcategory}
                            label={texts.CATEGORIE_DE_PARTS}
                            onChange={handleChange('subcategory')}
                            inputStyle={classes.input}
                        >
                            <MenuItem value="all">{texts.ALL_2}</MenuItem>
                            {shareSubcategories.map(sc => (
                                <MenuItem key={sc.id} value={sc.id}>
                                    {sc.name}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                        {(userIsServicer || userIsProvider || userIsPromoter) && (
                            <CustomSelect
                                disabled={fetchingCompanies}
                                value={customFilters.companyId}
                                label={texts.PARTNER}
                                onChange={handleChange('companyId')}
                                inputStyle={classes.input}
                            >
                                <MenuItem value="all">{texts.ALL_1}</MenuItem>
                                {!userIsNetworkAdmin && !userIsProvider && !userIsPromoter && (
                                    <MenuItem value={0}>{texts.PEQAN}</MenuItem>
                                )}
                                {processedDistributorCompanies.map(
                                    ({ 0: { id, corporateName } }) => (
                                        <MenuItem key={id} value={id}>
                                            {corporateName}
                                        </MenuItem>
                                    )
                                )}
                            </CustomSelect>
                        )}
                        <CustomSelect
                            value={customFilters.investorClassification}
                            label={texts.STATUT}
                            onChange={handleChange('investorClassification')}
                            inputStyle={classes.input}
                        >
                            <MenuItem value="all">{texts.ALL_1}</MenuItem>
                            {Object.keys(investorClassifications).map(ic => (
                                <MenuItem key={ic} value={ic}>
                                    {investorClassifications[ic]}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                    </Box>
                    <Box display="flex" flexWrap="wrap" mb={2} gap="16px">
                        <CustomSelect
                            value={customFilters.distributorUser}
                            label={texts.CONSULTANT}
                            onChange={handleChange('distributorUser')}
                            inputStyle={classes.input}
                            disabled={
                                !customFilters.companyId ||
                                (customFilters.companyId === 'all' && distributorUsersLoader) ||
                                distributorSubscriptionsLoader
                            }
                        >
                            <MenuItem value="all">{texts.ALL_1}</MenuItem>
                            {userIsDistributor
                                ? distributorSubscriptions.map(ds => (
                                      <MenuItem key={ds.user.id} value={ds.user.id}>
                                          {parseFullName(ds.user)}
                                      </MenuItem>
                                  ))
                                : sortedDistributorUsers.map(u => (
                                      <MenuItem key={u.id} value={u.id}>
                                          {parseFullName(u)}
                                      </MenuItem>
                                  ))}
                        </CustomSelect>
                        <CustomTextField
                            label={texts.COMPLEMENT}
                            placeholder={texts.COMPLEMENT}
                            inputStyle={classes.input}
                            value={customFilters.tag}
                            onChange={handleChange('tag')}
                        />
                        {userIsServicer && displaySubscriptionStatus && (
                            <CustomSelect
                                value={customFilters.subscriptionStatus}
                                label={texts.SUBSCRIPTIONS}
                                onChange={handleChange('subscriptionStatus')}
                                inputStyle={classes.input}
                            >
                                <MenuItem value="all">{texts.ALL_1}</MenuItem>
                                {Object.keys(subscriptionStatusLabels).map(ic => (
                                    <MenuItem key={ic} value={ic}>
                                        {subscriptionStatusLabels[ic]}
                                    </MenuItem>
                                ))}
                            </CustomSelect>
                        )}
                    </Box>
                    <Box display="flex" flexWrap="wrap" mb={2} gap="16px">
                        {userIsServicer && (
                            <CustomSelect
                                disabled={promotersFetching}
                                value={customFilters.promoter}
                                label={texts.PROMOTER}
                                onChange={handleChange('promoter')}
                                inputStyle={classes.input}
                            >
                                <MenuItem value="all">{texts.ALL_1}</MenuItem>
                                {promoters.map(p => (
                                    <MenuItem key={p.id} value={p.id}>
                                        {p.name}
                                    </MenuItem>
                                ))}
                            </CustomSelect>
                        )}
                        {userIsServicer && (
                            <CustomSelect
                                disabled={providersFetching}
                                value={customFilters.provider}
                                label={texts.PROVIDER}
                                onChange={handleChange('provider')}
                                inputStyle={classes.input}
                            >
                                <MenuItem value="all">{texts.ALL_1}</MenuItem>
                                {providers.map(p => (
                                    <MenuItem key={p.id} value={p.id}>
                                        {p.name}
                                    </MenuItem>
                                ))}
                            </CustomSelect>
                        )}
                    </Box>
                    <Box textAlign="right">
                        <CancelButton disabled={loading} onClick={handleCancel}>
                            {texts.CANCEL}
                        </CancelButton>
                        <ActionButton
                            loading={loading}
                            disabled={loading}
                            onClick={handleApplyFilters}
                        >
                            {texts.APPLY}
                        </ActionButton>
                    </Box>
                </Loader>
            </Paper>
        </Collapse>
    );
};

CustomFilters.propTypes = {
    open: PropTypes.bool,
    applyFilters: PropTypes.func,
    savedFilters: PropTypes.object,
    defaultFilters: PropTypes.object,
    loading: PropTypes.bool,
    ongoing: PropTypes.bool,
    displaySubscriptionStatus: PropTypes.bool,
};

export default CustomFilters;
